/// <amd-module name="WIT/ProcessTemplateSelector/ProcessTemplateSelector" />
import * as React from "react";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { VssComponent, Components, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { EmptyGuidString } from "VSS/Core/Util/String";
import { LinkCallout } from "WIT/ProcessTemplateSelector/LinkCallout";
import * as Resources from "WIT/ProcessTemplateSelector/Resources";
import { IChildOptionItem, IOptionItem, SimpleComboBox } from "WIT/ProcessTemplateSelector/SimpleComboBox";

interface IProcessTemplateDescriptor {
    id: string;
    name: string;
    description: string;
    isDefault: boolean;
    typeId: string;
    inherits: string;
    isSystemTemplate: boolean;
}

interface ProcessTemplateSelectorState {
    processTemplates: IProcessTemplateDescriptor[];
    processTemplatesOptionItems: IOptionItem[];
    currentProcessTemplate: IProcessTemplateDescriptor;
}

interface ProcessTemplateSelectorProps extends IVssComponentProperties {
    onSelectedOptionChanged: (key: string, value: string) => void;
    initialOptions?: { [key: string]: string };
    disabled?: boolean;
}

class ProcessTemplateSelector extends VssComponent<ProcessTemplateSelectorProps, ProcessTemplateSelectorState> {
    private static PROVIDER_ID = "ms.vss-work-web.wit-process-template-provider";
    private static PROCESS_TEMPLATE_SELECTOR_KEY = "processTemplateSelector";

    constructor(props: ProcessTemplateSelectorProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            processTemplates: [],
            processTemplatesOptionItems: [],
            currentProcessTemplate: {} as IProcessTemplateDescriptor
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._loadProcessTemplates();
    }

    public render(): JSX.Element | null {
        const showProcessTemplateDescriptionCallout = !this.state.currentProcessTemplate.isSystemTemplate
            && this.state.currentProcessTemplate.description;

        return <div className={this.props.className}>
            {
                this.state.processTemplatesOptionItems.length > 0 &&
                <>
                    <SimpleComboBox
                        ariaLabel={Resources.ProcessTemplateToolTip}
                        label={Resources.WorkItemProcessSectionHeader}
                        helpIconProps={{
                            ariaLabel: Resources.WorkItemProcessLearnMoreAriaLabel,
                            href: Resources.ProcessTemplateLearnMoreLink,
                            tooltip: Resources.ProcessTemplateHelpToolTip,
                        }}
                        sourceItems={this.state.processTemplatesOptionItems}
                        defaultSelectedKey={this.state.currentProcessTemplate.typeId}
                        disabled={this.props.disabled}
                        onOptionChanged={this._onOptionChanged}
                        className={"process-template-selector-dropdown"}
                        calloutClassName={"process-template-selector-callout"}
                    />
                    {
                        showProcessTemplateDescriptionCallout &&
                        <LinkCallout
                            linkText={Resources.ShowDescriptionText}
                            ariaLabel={Resources.ShowDescriptionLinkAriaLabel}
                            calloutTitle={this.state.currentProcessTemplate.name}
                            calloutText={this.state.currentProcessTemplate.description} />
                    }
                </>
            }
        </div>;
    }

    private _onOptionChanged = (value: string): void => {
        const currentProcessTemplate = this.state.processTemplates.find(template => template.typeId === value) || this.state.currentProcessTemplate;
        this.props.onSelectedOptionChanged(ProcessTemplateSelector.PROCESS_TEMPLATE_SELECTOR_KEY, currentProcessTemplate.typeId);
        this.setState({ currentProcessTemplate: currentProcessTemplate });
    }

    private _loadProcessTemplates(): void {
        this.trackPromise(this.context.pageContext.getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<IProcessTemplateDescriptor[]>(ProcessTemplateSelector.PROVIDER_ID, undefined, true)).promise
            .then((processTemplates: IProcessTemplateDescriptor[] | undefined) => {
                if (processTemplates && processTemplates && processTemplates.length > 0) {
                    let defaultSelectedKey: string | null = null;
                    const processTemplatesOptionItems = this._buildProcessTemplateTree(processTemplates);
                    if (this.props.initialOptions) {
                        defaultSelectedKey = this.props.initialOptions[ProcessTemplateSelector.PROCESS_TEMPLATE_SELECTOR_KEY];
                    }
                    const defaultProcessTemplate = processTemplates
                        .find(template => ((!defaultSelectedKey && template.isDefault) || (template.name === defaultSelectedKey)))
                        || processTemplates[0];                         

                    this.setState({
                        processTemplates: processTemplates,
                        processTemplatesOptionItems: processTemplatesOptionItems,
                        currentProcessTemplate: defaultProcessTemplate,
                    });

                    this.props.onSelectedOptionChanged(ProcessTemplateSelector.PROCESS_TEMPLATE_SELECTOR_KEY, defaultProcessTemplate.typeId);
                }
            }, () => {
                // no-op
            });
    }

    /**
     * Build the parent-child relation between the process templates
     * @param processTemplates - the process templates got from the props
     */
    private _buildProcessTemplateTree(processTemplates: IProcessTemplateDescriptor[]): IOptionItem[] {
        const processTemplateTree: IOptionItem[] = [];
        const itemMap: { [guid: string]: IOptionItem } = {};

        // Process root level templates
        for (let i = 0; i < processTemplates.length; i++) {
            let template = processTemplates[i];
            // If the template is a root level template and does not have a parent
            if (template.inherits === EmptyGuidString || !template.inherits) {
                const typeId = template.typeId;
                let item: IOptionItem = {
                    text: template.name,
                    key: typeId,
                    children: []
                };

                processTemplateTree.push(item);
                itemMap[typeId] = item;
            }
        }

        // Process inherited templates
        for (let i = 0; i < processTemplates.length; i++) {
            const template = processTemplates[i];
            // If the template is not a root level template and has a parent
            if (template.inherits !== EmptyGuidString && template.inherits) {
                const item = itemMap[template.inherits];

                // Though this template inherits another template, that parent template is not available.
                // Hence make this a parent level template
                if (!item) {
                    const parentItem: IOptionItem = {
                        text: template.name,
                        key: template.typeId
                    };
                    processTemplateTree.push(parentItem);
                } else {
                    let children = item.children;
                    if (children == null) {
                        item.children = children = [];
                    }
                    children.push({
                        text: template.name,
                        key: template.typeId
                    });
                }
            }
        }
        return processTemplateTree;
    }
}

Components.add("WIT.ProcessTemplateSelector", ProcessTemplateSelector);
